import { PartImageItem, PartImageItemRegion } from '@/api/generatedApi';
import { useEffect, useState } from 'react';

export const MAX_CLIPS_PER_ITEM = 3;
const CLIP_MARGIN = 0.1;
const CLIP_MAX_SIDE = 480;

export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const clamp = (value: number, max: number): number => Math.min(Math.max(value, 0), max);

export function paddedCropRect(region: PartImageItemRegion, imageWidth: number, imageHeight: number): PixelRect {
  const marginX = region.width * CLIP_MARGIN;
  const marginY = region.height * CLIP_MARGIN;
  const left = clamp(region.left - marginX, 1);
  const top = clamp(region.top - marginY, 1);
  const right = clamp(region.left + region.width + marginX, 1);
  const bottom = clamp(region.top + region.height + marginY, 1);
  return {
    x: Math.round(left * imageWidth),
    y: Math.round(top * imageHeight),
    width: Math.max(1, Math.round((right - left) * imageWidth)),
    height: Math.max(1, Math.round((bottom - top) * imageHeight)),
  };
}

async function cropToUrl(bitmap: ImageBitmap, region: PartImageItemRegion): Promise<string> {
  const rect = paddedCropRect(region, bitmap.width, bitmap.height);
  const scale = Math.min(1, CLIP_MAX_SIDE / Math.max(rect.width, rect.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(rect.width * scale);
  canvas.height = Math.round(rect.height * scale);
  canvas
    .getContext('2d')
    ?.drawImage(bitmap, rect.x, rect.y, rect.width, rect.height, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  return blob ? URL.createObjectURL(blob) : '';
}

export interface PartImageItemClip {
  photoNumber: number;
  url: string;
}

const clipRegions = (item: PartImageItem): PartImageItemRegion[] => item.regions.slice(0, MAX_CLIPS_PER_ITEM);

async function buildClips(images: File[], items: PartImageItem[]): Promise<PartImageItemClip[][]> {
  const photoNumbers = [...new Set(items.flatMap(item => clipRegions(item).map(region => region.photoNumber)))];
  const bitmaps = new Map<number, ImageBitmap>();
  await Promise.all(
    photoNumbers.map(async photoNumber => {
      const file = images[photoNumber - 1];
      if (file) bitmaps.set(photoNumber, await createImageBitmap(file, { imageOrientation: 'from-image' }));
    })
  );
  const clips = await Promise.all(
    items.map(item =>
      Promise.all(
        clipRegions(item).flatMap(region => {
          const bitmap = bitmaps.get(region.photoNumber);
          return bitmap ? [cropToUrl(bitmap, region).then(url => ({ photoNumber: region.photoNumber, url }))] : [];
        })
      )
    )
  );
  for (const bitmap of bitmaps.values()) bitmap.close();
  return clips.map(itemClips => itemClips.filter(clip => clip.url));
}

const revokeAll = (clips: PartImageItemClip[][]) => {
  for (const clip of clips.flat()) URL.revokeObjectURL(clip.url);
};

export function usePartImageItemClips(images: File[], items: PartImageItem[]): PartImageItemClip[][] {
  const [clips, setClips] = useState<PartImageItemClip[][]>([]);
  useEffect(() => {
    let cancelled = false;
    let built: PartImageItemClip[][] = [];
    buildClips(images, items)
      .then(result => {
        built = result;
        if (cancelled) revokeAll(result);
        else setClips(result);
      })
      .catch(() => setClips([]));
    return () => {
      cancelled = true;
      revokeAll(built);
    };
  }, [images, items]);
  return clips;
}
